/**
 * serialize.ts — bidirectional adapter between the visual builder's
 * ergonomic internal state and the canonical {@link StepSpec}/{@link TriggerSpec}
 * shapes consumed by the API.
 *
 * Why a dedicated adapter?
 *
 *   - The structured form composer (StepInspector) stores a step's actions
 *     as a single `ActionSpec[]` where each row already represents a
 *     concrete channel + recipient + template. The visual builder, on the
 *     other hand, exposes a friendlier *row* abstraction where one row can
 *     fan out across multiple channels (email + WhatsApp) — that's what the
 *     user spec'd ("WHEN ... send [Email + WhatsApp] to [Title Company]").
 *     On save we expand each multi-channel row into 1-N ActionSpec entries.
 *
 *   - The structured composer stores guard conditions as a recursive
 *     `ConditionSpec` AST. The visual builder gives the user a flat AND
 *     chain of comparison rows — easier to drag a field onto. On save we
 *     fold the rows back into the same AST shape.
 *
 *   - Round-trip is lossless for anything the visual builder is *capable*
 *     of authoring. Steps with nested boolean groups, NOT, OR-of-groups
 *     etc. come back from the server as `unsupportedGuard: true` rows so
 *     the user knows to edit them in the structured tab instead of
 *     silently flattening their work.
 *
 * Public entry points:
 *   - {@link stepSpecsToVisualSteps}     hydrate from server data
 *   - {@link visualStepsToStepSpecs}     prepare for save
 *   - {@link triggerSpecsToVisualTriggers} / {@link visualTriggersToTriggerSpecs}
 *
 * All helpers are pure / side-effect free.
 */

import type {
  ActionSpec,
  ComparisonCondition,
  ConditionLiteral,
  ConditionOperator,
  ConditionSpec,
  EmailRecipient,
  ExistenceCondition,
  PhoneRecipient,
  StepCategory,
  StepSpec,
  TriggerSpec,
} from '@mana/shared';
import {
  FIELD_CATALOG_BY_ID,
  FIELD_CATALOG_BY_PATH,
  isEmailRecipient,
  isPhoneRecipient,
  operatorsForType,
  type LoanFieldDescriptor,
} from '@mana/shared';

// ---------------------------------------------------------------------------
// Visual builder internal model
// ---------------------------------------------------------------------------

export type NotifyChannel = 'email' | 'whatsapp' | 'sms';

/** A single drag-built condition row (one field comparison). */
export interface VisualConditionRow {
  /** Stable id within a step — generated client-side for React keys. */
  id: string;
  /** Catalog descriptor id (e.g. "outstandingCondition"). May be null when the
   *  server-stored path doesn't match anything in the catalog (legacy / hand-
   *  written guards); the row then falls back to the raw path. */
  fieldId: string | null;
  /** Dot path the runtime evaluator reads. Mirrors `field.path` when known. */
  path: string;
  op: ConditionOperator;
  /** Undefined for existence ops; a primitive for comparison ops. */
  value: ConditionLiteral | undefined;
}

/** A single drag-built action row — may emit multiple ActionSpec entries on
 *  save when more than one channel and/or more than one recipient is
 *  selected. A row with recipients=[A, B] and channels={email, whatsapp}
 *  expands to up to 4 ActionSpec entries (each recipient × each channel that
 *  recipient supports). */
export interface VisualActionRow {
  id: string;
  /** Channels selected on the row. At least one must be true at save time. */
  channels: { email: boolean; whatsapp: boolean; sms: boolean };
  /**
   * Role-keyed recipients. Strings stay untagged here and are validated at
   * save time. At least one must be present; the UI prevents empty arrays.
   */
  recipients: string[];
  /** Free-form email/phone shown when 'custom' is one of the recipients. */
  customTo?: string;
  /** Template key reused across the row's emitted ActionSpec entries. */
  templateKey: string;
}

/**
 * A condition GROUP — a parenthesized set of rows combined by `logic`.
 * Groups let the builder express two-level boolean logic like
 * `(rowA AND rowB) OR (rowC)`: two groups combined by the step's `groupLogic`,
 * each group combining its own rows by the group's `logic`.
 */
export interface VisualConditionGroup {
  /** Stable id within a step — generated client-side for React keys + drop targets. */
  id: string;
  /** How this group's own rows combine. */
  logic: 'and' | 'or';
  rows: VisualConditionRow[];
}

/** A step in the visual builder. Mirrors {@link StepSpec} but with the
 *  rebroken-out condition groups + multi-channel action rows. */
export interface VisualStep {
  /** Stable within workflow; gets serialized as {@link StepSpec.key}. */
  key: string;
  name: string;
  category: StepCategory;
  cooldownHours: number;
  isTerminal: boolean;
  /** How the groups combine with each other (the top-level operator). */
  groupLogic: 'and' | 'or';
  /** One or more condition groups. Always at least one (possibly empty). */
  conditionGroups: VisualConditionGroup[];
  actionRows: VisualActionRow[];
  /** True when the server step had a guard the builder can't faithfully
   *  represent (nested groups / NOT). We surface this as a banner and refuse
   *  to overwrite the guard on save (we re-emit the original). */
  unsupportedGuard: boolean;
  /** The original guard, preserved verbatim when `unsupportedGuard` is true. */
  originalGuard?: ConditionSpec;
}

/** Trigger shape inside the builder. Time + event triggers reuse the
 *  spec shape directly; the condition trigger gets the flat row treatment. */
export interface VisualTrigger {
  id: string;
  spec: TriggerSpec;
  /** Only meaningful for `spec.type === 'condition'` — drives the right-rail
   *  predicate row editor. */
  predicateRows?: VisualConditionRow[];
  predicateLogic?: 'and' | 'or';
  predicateUnsupported?: boolean;
}

// ---------------------------------------------------------------------------
// id generation — small, deterministic-enough for React keys
// ---------------------------------------------------------------------------

let _seq = 0;
function nextId(prefix: string): string {
  _seq += 1;
  return `${prefix}_${Date.now().toString(36)}_${_seq.toString(36)}`;
}

// ---------------------------------------------------------------------------
// Condition row helpers
// ---------------------------------------------------------------------------

const COMPARISON_OPS = new Set<ConditionOperator>([
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
]);
const EXISTENCE_OPS = new Set<ConditionOperator>(['exists', 'not_exists']);

/** Default operator for a freshly-dropped field. Numeric → `gt`,
 *  boolean_yn / enum → `eq`, dates → `gt` (after), strings → `eq`. */
export function defaultOpForField(field: LoanFieldDescriptor): ConditionOperator {
  const allowed = operatorsForType(field.type);
  switch (field.type) {
    case 'number':
    case 'integer':
      return allowed.includes('gt') ? 'gt' : (allowed[0] ?? 'eq');
    case 'date':
    case 'datetime':
      return allowed.includes('gt') ? 'gt' : (allowed[0] ?? 'eq');
    case 'boolean_yn':
    case 'enum':
    case 'string':
    default:
      return allowed.includes('eq') ? 'eq' : (allowed[0] ?? 'eq');
  }
}

/** Default literal for a freshly-dropped field + operator combo. */
export function defaultValueForField(
  field: LoanFieldDescriptor,
  op: ConditionOperator,
): ConditionLiteral | undefined {
  if (EXISTENCE_OPS.has(op)) return undefined;
  switch (field.type) {
    case 'number':
    case 'integer':
      return 0;
    case 'boolean_yn':
      return 'Y';
    case 'enum':
      return field.enumValues?.[0]?.value ?? '';
    case 'date':
    case 'datetime':
    case 'string':
    default:
      return '';
  }
}

export function makeConditionRowFromField(
  field: LoanFieldDescriptor,
): VisualConditionRow {
  const op = defaultOpForField(field);
  return {
    id: nextId('cond'),
    fieldId: field.id,
    path: field.path,
    op,
    value: defaultValueForField(field, op),
  };
}

/** A fresh empty condition group (defaults to AND within the group). */
export function makeEmptyGroup(logic: 'and' | 'or' = 'and'): VisualConditionGroup {
  return { id: nextId('grp'), logic, rows: [] };
}

/** Build a fresh "todo" action row. Defaults to email → borrower. */
export function makeEmptyActionRow(): VisualActionRow {
  return {
    id: nextId('act'),
    channels: { email: true, whatsapp: false, sms: false },
    recipients: ['borrower'],
    templateKey: 'default',
  };
}

// ---------------------------------------------------------------------------
// Server → visual: hydration
// ---------------------------------------------------------------------------

/**
 * Try to flatten a server-stored guard into the builder's flat AND/OR rows.
 * Returns `{ rows: [...], logic, unsupported: false }` on success.
 * When the guard contains nested groups, NOT, or any non-leaf child of an
 * AND/OR, we mark it `unsupported` and the builder shows a banner.
 */
export function flattenGuard(
  guard: ConditionSpec | undefined,
): {
  rows: VisualConditionRow[];
  logic: 'and' | 'or';
  unsupported: boolean;
} {
  if (!guard) return { rows: [], logic: 'and', unsupported: false };

  const asRow = (c: ConditionSpec): VisualConditionRow | null => {
    if (
      c.op === 'eq' ||
      c.op === 'neq' ||
      c.op === 'gt' ||
      c.op === 'gte' ||
      c.op === 'lt' ||
      c.op === 'lte'
    ) {
      return conditionToRow(c);
    }
    if (c.op === 'exists' || c.op === 'not_exists') {
      return existenceToRow(c);
    }
    return null;
  };

  if (guard.op === 'and' || guard.op === 'or') {
    const rows: VisualConditionRow[] = [];
    for (const child of guard.conditions) {
      const r = asRow(child);
      if (r === null) return { rows: [], logic: 'and', unsupported: true };
      rows.push(r);
    }
    return { rows, logic: guard.op, unsupported: false };
  }

  const single = asRow(guard);
  if (single === null) {
    return { rows: [], logic: 'and', unsupported: true };
  }
  return { rows: [single], logic: 'and', unsupported: false };
}

/**
 * Parse a server guard into the builder's two-level group model.
 *   - undefined        → no groups (caller seeds one empty group).
 *   - leaf comparison  → one group, one row.
 *   - flat and/or      → one group whose `logic` is the operator (back-compat
 *                        with the previous flat model — round-trips identically).
 *   - two-level and/or  → groupLogic = top operator; each child that is a leaf
 *                        becomes a single-row group, each child that is a flat
 *                        and/or-of-leaves becomes a multi-row group.
 *   - anything deeper / NOT → `unsupported: true` (structured-form fallback).
 */
export function parseGuardGroups(guard: ConditionSpec | undefined): {
  groupLogic: 'and' | 'or';
  groups: VisualConditionGroup[];
  unsupported: boolean;
} {
  if (!guard) return { groupLogic: 'or', groups: [], unsupported: false };

  const asRow = (c: ConditionSpec): VisualConditionRow | null => {
    if (
      c.op === 'eq' ||
      c.op === 'neq' ||
      c.op === 'gt' ||
      c.op === 'gte' ||
      c.op === 'lt' ||
      c.op === 'lte'
    ) {
      return conditionToRow(c);
    }
    if (c.op === 'exists' || c.op === 'not_exists') {
      return existenceToRow(c);
    }
    return null;
  };

  // A child of the top operator: either a leaf (→ 1-row group) or a flat
  // and/or-of-leaves (→ multi-row group). Returns null when un-flattenable.
  const asGroup = (c: ConditionSpec): VisualConditionGroup | null => {
    const leaf = asRow(c);
    if (leaf) return { id: nextId('grp'), logic: 'and', rows: [leaf] };
    if (c.op === 'and' || c.op === 'or') {
      const rows: VisualConditionRow[] = [];
      for (const child of c.conditions) {
        const r = asRow(child);
        if (r === null) return null; // nested group inside a group → unsupported
        rows.push(r);
      }
      return { id: nextId('grp'), logic: c.op, rows };
    }
    return null;
  };

  // Single leaf at the top.
  const topLeaf = asRow(guard);
  if (topLeaf) {
    return {
      groupLogic: 'or',
      groups: [{ id: nextId('grp'), logic: 'and', rows: [topLeaf] }],
      unsupported: false,
    };
  }

  if (guard.op === 'and' || guard.op === 'or') {
    // Are all children leaves? → single flat group (back-compat).
    const allLeaves = guard.conditions.every((c) => asRow(c) !== null);
    if (allLeaves) {
      const rows = guard.conditions.map((c) => asRow(c)!).filter(Boolean);
      return {
        groupLogic: 'or',
        groups: [{ id: nextId('grp'), logic: guard.op, rows }],
        unsupported: false,
      };
    }
    // Mixed children → treat the top operator as the group combinator.
    const groups: VisualConditionGroup[] = [];
    for (const child of guard.conditions) {
      const g = asGroup(child);
      if (g === null) return { groupLogic: 'or', groups: [], unsupported: true };
      groups.push(g);
    }
    return { groupLogic: guard.op, groups, unsupported: false };
  }

  // NOT or anything else.
  return { groupLogic: 'or', groups: [], unsupported: true };
}

/**
 * Fold the builder's groups back into a single {@link ConditionSpec}.
 *   - empty groups are dropped.
 *   - 0 valid groups → undefined (no guard).
 *   - 1 valid group  → that group's guard directly (no extra wrapper — keeps
 *                      flat guards byte-stable on round-trip).
 *   - N valid groups → { op: groupLogic, conditions: [...group guards] }.
 */
export function groupsToGuard(
  groups: readonly VisualConditionGroup[],
  groupLogic: 'and' | 'or',
): ConditionSpec | undefined {
  const groupGuards = groups
    .map((g) => rowsToGuard(g.rows, g.logic))
    .filter((c): c is ConditionSpec => c !== undefined);
  if (groupGuards.length === 0) return undefined;
  if (groupGuards.length === 1) return groupGuards[0];
  return groupLogic === 'and'
    ? { op: 'and', conditions: groupGuards }
    : { op: 'or', conditions: groupGuards };
}

function conditionToRow(c: ComparisonCondition): VisualConditionRow {
  const field = FIELD_CATALOG_BY_PATH[c.path];
  return {
    id: nextId('cond'),
    fieldId: field?.id ?? null,
    path: c.path,
    op: c.op,
    value: c.value,
  };
}

function existenceToRow(c: ExistenceCondition): VisualConditionRow {
  const field = FIELD_CATALOG_BY_PATH[c.path];
  return {
    id: nextId('cond'),
    fieldId: field?.id ?? null,
    path: c.path,
    op: c.op,
    value: undefined,
  };
}

/**
 * Group a step's actions into multi-channel rows. Heuristic: actions that
 * share `templateKey` AND `to` AND (`customTo` if present) collapse into
 * one row with the union of their channels.
 *
 * Multi-recipient rows are a save-side fan-out — on hydration we keep each
 * recipient as its own row so the round trip is byte-stable when the user
 * just opens and re-saves. The user can merge recipients into a single row
 * in the UI when they want a one-time consolidated view.
 *
 * Internal-task actions are surfaced as their own non-notification rows —
 * Phase 1's visual builder is a NOTIFY-only tool, so we keep them in
 * `extras` and pass them through verbatim on save.
 */
export function groupActionsToRows(actions: ActionSpec[]): {
  rows: VisualActionRow[];
  /** Actions the visual builder doesn't surface (internal_task). */
  extras: ActionSpec[];
} {
  const rows: VisualActionRow[] = [];
  const extras: ActionSpec[] = [];

  for (const a of actions) {
    if (a.kind === 'internal_task') {
      extras.push(a);
      continue;
    }
    const target = a.to;
    const custom = a.customTo ?? '';
    const tpl = a.templateKey;
    // Match an existing row that already represents this exact recipient/
    // template/customTo triple — fold channels into it.
    const existing = rows.find(
      (r) =>
        r.recipients.length === 1 &&
        r.recipients[0] === target &&
        (r.customTo ?? '') === custom &&
        r.templateKey === tpl,
    );
    if (existing) {
      existing.channels[a.kind] = true;
      continue;
    }
    rows.push({
      id: nextId('act'),
      channels: {
        email: a.kind === 'email',
        whatsapp: a.kind === 'whatsapp',
        sms: a.kind === 'sms',
      },
      recipients: [target],
      customTo: custom || undefined,
      templateKey: tpl,
    });
  }

  return { rows, extras };
}

/** Hydrate the builder's full step list from server-shaped {@link StepSpec}s. */
export function stepSpecsToVisualSteps(
  steps: readonly StepSpec[],
): { visualSteps: VisualStep[]; extrasByStepKey: Record<string, ActionSpec[]> } {
  const extrasByStepKey: Record<string, ActionSpec[]> = {};
  const visualSteps = steps.map((s): VisualStep => {
    const parsed = parseGuardGroups(s.guardJson);
    const grouped = groupActionsToRows(s.actions);
    extrasByStepKey[s.key] = grouped.extras;
    // Always present the user with at least one (possibly empty) group so the
    // WHEN zone always has a drop target.
    const groups = parsed.unsupported
      ? []
      : parsed.groups.length > 0
        ? parsed.groups
        : [makeEmptyGroup()];
    return {
      key: s.key,
      name: s.name,
      category: s.category,
      cooldownHours: s.cooldownHours,
      isTerminal: s.isTerminal,
      groupLogic: parsed.groupLogic,
      conditionGroups: groups,
      actionRows: grouped.rows,
      unsupportedGuard: parsed.unsupported,
      originalGuard: parsed.unsupported ? s.guardJson : undefined,
    };
  });
  return { visualSteps, extrasByStepKey };
}

// ---------------------------------------------------------------------------
// Visual → server: save-side serialization
// ---------------------------------------------------------------------------

/**
 * Fold flat condition rows back into a single {@link ConditionSpec} AST.
 * Returns `undefined` when there are no rows (no guard).
 */
export function rowsToGuard(
  rows: readonly VisualConditionRow[],
  logic: 'and' | 'or',
): ConditionSpec | undefined {
  const valid = rows
    .map(rowToCondition)
    .filter((c): c is ConditionSpec => c !== null);
  if (valid.length === 0) return undefined;
  if (valid.length === 1) return valid[0];
  return logic === 'and'
    ? { op: 'and', conditions: valid }
    : { op: 'or', conditions: valid };
}

function rowToCondition(row: VisualConditionRow): ConditionSpec | null {
  if (row.path.length === 0) return null;
  if (EXISTENCE_OPS.has(row.op)) {
    return { op: row.op as 'exists' | 'not_exists', path: row.path };
  }
  if (!COMPARISON_OPS.has(row.op)) return null;
  // gt/gte/lt/lte require numeric literals; coerce strings if needed.
  if (row.op === 'gt' || row.op === 'gte' || row.op === 'lt' || row.op === 'lte') {
    const n = coerceNumber(row.value);
    if (n === null) return null;
    return { op: row.op, path: row.path, value: n };
  }
  // eq / neq — leave the literal as-is, modulo undefined → null.
  const v: ConditionLiteral =
    row.value === undefined ? null : (row.value as ConditionLiteral);
  return { op: row.op as 'eq' | 'neq', path: row.path, value: v };
}

function coerceNumber(value: ConditionLiteral | undefined): number | null {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Expand an action row into 1-N {@link ActionSpec} entries. For each
 * recipient on the row, emit one spec per channel the recipient supports
 * (e.g. processor + SMS is dropped because PHONE_RECIPIENTS excludes
 * processor). Drops the row silently when no channels are selected — the
 * caller is expected to keep the UI from emitting one in that state.
 *
 * De-duplicates against `(kind, recipient, customTo)` so opening a multi-
 * channel row containing 'custom' twice doesn't double-emit the spec.
 */
export function actionRowToSpecs(row: VisualActionRow): ActionSpec[] {
  const out: ActionSpec[] = [];
  const customTo = row.customTo?.trim();
  const templateKey = row.templateKey || 'default';
  const seen = new Set<string>();
  for (const recipient of row.recipients) {
    const key = (kind: string) => `${kind}|${recipient}|${customTo ?? ''}`;
    if (row.channels.email && isEmailRecipient(recipient as EmailRecipient)) {
      if (!seen.has(key('email'))) {
        seen.add(key('email'));
        out.push({
          kind: 'email',
          templateKey,
          to: recipient as EmailRecipient,
          ...(recipient === 'custom' && customTo ? { customTo } : {}),
        });
      }
    }
    if (row.channels.whatsapp && isPhoneRecipient(recipient as PhoneRecipient)) {
      if (!seen.has(key('whatsapp'))) {
        seen.add(key('whatsapp'));
        out.push({
          kind: 'whatsapp',
          templateKey,
          to: recipient as PhoneRecipient,
          ...(recipient === 'custom' && customTo ? { customTo } : {}),
        });
      }
    }
    if (row.channels.sms && isPhoneRecipient(recipient as PhoneRecipient)) {
      if (!seen.has(key('sms'))) {
        seen.add(key('sms'));
        out.push({
          kind: 'sms',
          templateKey,
          to: recipient as PhoneRecipient,
          ...(recipient === 'custom' && customTo ? { customTo } : {}),
        });
      }
    }
  }
  return out;
}

/**
 * Serialize the full visual step list back into {@link StepSpec}s ready to
 * PATCH onto the workflow.
 */
export function visualStepsToStepSpecs(
  visualSteps: readonly VisualStep[],
  extrasByStepKey: Readonly<Record<string, ActionSpec[]>>,
): StepSpec[] {
  return visualSteps.map((vs) => {
    const guard = vs.unsupportedGuard
      ? vs.originalGuard
      : groupsToGuard(vs.conditionGroups, vs.groupLogic);
    const generated = vs.actionRows.flatMap(actionRowToSpecs);
    const extras = extrasByStepKey[vs.key] ?? [];
    return {
      key: vs.key,
      name: vs.name,
      category: vs.category,
      cooldownHours: vs.cooldownHours,
      isTerminal: vs.isTerminal,
      ...(guard !== undefined ? { guardJson: guard } : {}),
      actions: [...generated, ...extras],
    };
  });
}

// ---------------------------------------------------------------------------
// Trigger adapters
// ---------------------------------------------------------------------------

export function triggerSpecsToVisualTriggers(
  triggers: readonly TriggerSpec[],
): VisualTrigger[] {
  return triggers.map((t): VisualTrigger => {
    if (t.type !== 'condition') {
      return { id: nextId('trig'), spec: t };
    }
    const flat = flattenGuard(t.predicate);
    return {
      id: nextId('trig'),
      spec: t,
      predicateRows: flat.rows,
      predicateLogic: flat.logic,
      predicateUnsupported: flat.unsupported,
    };
  });
}

export function visualTriggersToTriggerSpecs(
  visualTriggers: readonly VisualTrigger[],
): TriggerSpec[] {
  return visualTriggers.map((vt) => {
    if (vt.spec.type !== 'condition') return vt.spec;
    if (vt.predicateUnsupported) return vt.spec;
    const predicate =
      rowsToGuard(vt.predicateRows ?? [], vt.predicateLogic ?? 'and') ?? {
        op: 'exists',
        path: 'loan.id',
      };
    return { ...vt.spec, predicate };
  });
}

// ---------------------------------------------------------------------------
// Lookup helper re-exports — components consume these without re-deriving.
// ---------------------------------------------------------------------------

export function lookupField(id: string | null): LoanFieldDescriptor | undefined {
  if (!id) return undefined;
  return FIELD_CATALOG_BY_ID[id];
}

export function lookupFieldByPath(path: string): LoanFieldDescriptor | undefined {
  return FIELD_CATALOG_BY_PATH[path];
}
